import { useState } from "react";
import { ChevronDown, Send } from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { toast } from "sonner";

const PAGES = {
  about: {
    title: "About SHIPANY",
    sub: "One Platform. Every Courier. Smarter Delivery.",
    sections: [
      { h: "Who we are", p: "SHIPANY is a multi-carrier logistics platform built in India for modern commerce. We unify leading courier aggregators and carriers behind one intelligent API, one wallet and one dashboard." },
      { h: "What we believe", p: "Sellers should never have to guess which courier is cheapest, fastest or most reliable for a given lane. Our pricing engine and normalized tracking make that decision automatic." },
      { h: "Where we're headed", p: "Phase 1 delivers the core shipping stack. Phase 2 brings deeper integrations, NDR automation and analytics. Phase 3 opens our orchestration network to enterprise workflows." },
    ],
  },
  features: {
    title: "Features",
    sub: "Everything you need to run shipping like a machine.",
    sections: [
      { h: "Rate comparison", p: "Zone-aware rate lookup across every enabled courier with Lowest Rate, Fastest and Top Rated badges." },
      { h: "Smart booking", p: "Multi-step booking wizard with volumetric weight, COD support and instant AWB assignment." },
      { h: "Documents", p: "Shipping labels, tax invoices and packing slips — preview, print or download as PDF." },
      { h: "Unified tracking", p: "All provider scans normalized into SHIPANY statuses with a public tracking page per AWB." },
      { h: "Wallet & billing", p: "Prepaid wallet, automatic deductions, instant refunds on cancellation and GST invoices." },
      { h: "Admin control tower", p: "Manage providers, couriers, rate cards, margins, wallets, logs and profitability." },
    ],
  },
  "domestic-shipping": {
    title: "Domestic Shipping",
    sub: "27,000+ pincodes across India, from metro lanes to remote zones.",
    sections: [
      { h: "Zone-based pricing", p: "Five transparent zones — intra-city, regional, metro-to-metro, rest of India and remote — so you always know what a lane costs before you ship." },
      { h: "Surface & air", p: "Economy surface couriers for heavy freight, premium air express for urgent parcels. COD supported on most lanes." },
      { h: "Pickup from your warehouse", p: "Configure multiple warehouses and schedule pickups directly from your dashboard." },
    ],
  },
  "international-shipping": {
    title: "International Shipping",
    sub: "Cross-border lanes with the same one-platform experience.",
    sections: [
      { h: "Global reach", p: "Ship worldwide through our provider network with documentation guidance baked into the booking flow." },
      { h: "Phase 1 note", p: "International booking is enabled in the shipment wizard; rates are quoted via our international-ready adapters as lanes go live." },
    ],
  },
  "business-shipping": {
    title: "Business Shipping",
    sub: "For D2C brands, marketplaces and high-volume ops teams.",
    sections: [
      { h: "Bulk shipping", p: "Upload a CSV of orders and dispatch hundreds of shipments in one run with automatic best-rate selection." },
      { h: "Multi-warehouse", p: "Route shipments from the nearest fulfilment center with per-warehouse address books." },
      { h: "Finance-grade billing", p: "GST invoices, COD reconciliation and profitability reporting built in." },
    ],
  },
  api: {
    title: "SHIPANY API",
    sub: "One REST API. Every courier behind it.",
    sections: [
      { h: "REST-first", p: "Calculate rates, create shipments, fetch AWBs, print labels and pull tracking — all over authenticated JSON endpoints under /api." },
      { h: "Webhooks", p: "Provider webhooks are verified with HMAC signatures and normalized into SHIPANY statuses before they touch your data." },
      { h: "Security", p: "Provider credentials live only on our servers. Your frontend never talks to a courier aggregator directly." },
    ],
  },
  integrations: {
    title: "Integrations",
    sub: "Connected to the carriers you already trust.",
    sections: [
      { h: "Carrier providers", p: "Multiple carrier providers are wired through our provider adapter layer. Enable, disable and prioritize them from the admin tower." },
      { h: "Coming soon", p: "Store connectors (Shopify, WooCommerce), channel sync and accounting exports are on the Phase 2 roadmap." },
    ],
  },
  pricing: {
    title: "Pricing",
    sub: "Transparent per-shipment pricing. No subscriptions in Phase 1.",
    sections: [
      { h: "Pay per shipment", p: "Recharge your wallet and pay only for what you ship. Every rate quote shows the final price inclusive of GST before you book." },
      { h: "What a rate includes", p: "Base freight by zone and weight slab, additional weight charges, COD fees where applicable, fuel surcharge and GST." },
      { h: "Cancellations", p: "Cancel before pickup. The full amount is refunded to your wallet as soon as the courier confirms cancellation; most confirmations are instant." },
    ],
  },
  faq: {
    title: "Frequently Asked Questions",
    sub: "Quick answers for shippers.",
    faq: [
      ["How do I book a shipment?", "Sign up, create a warehouse, add funds to your wallet, then use Create Shipment: enter delivery details and package specs, compare courier rates and book. Your AWB and label are generated instantly."],
      ["Which couriers are available?", "SHIPANY aggregates couriers from multiple carrier networks. The exact list for your lane appears in the rate comparison step."],
      ["How does the wallet work?", "Shipping charges are deducted from your prepaid wallet at booking time. Cancellations before pickup are refunded as soon as the courier confirms cancellation."],
      ["Is COD supported?", "Yes — most couriers support COD. COD fees are shown transparently in every quote."],
      ["How do I track a shipment?", "Use the Track page with your AWB, or open tracking from your dashboard. No login required for tracking."],
    ],
  },
  blog: {
    title: "SHIPANY Blog",
    sub: "Notes on logistics, shipping economics and product updates.",
    sections: [
      { h: "Why multi-carrier wins", p: "Single-carrier lock-in costs Indian sellers up to 18% in avoidable freight spend. Here's the math behind lane-level courier selection." },
      { h: "Decoding shipping zones", p: "Intra-city vs metro-to-metro vs remote: how zone-based pricing actually works and how to price your products around it." },
      { h: "NDR is a solvable problem", p: "Most non-delivery reports are address or contactability issues. A look at how normalized NDR tracking cuts RTO rates." },
    ],
  },
  terms: {
    title: "Terms of Service", sub: "Last updated: June 2026",
    sections: [
      { h: "1. Services", p: "SHIPANY provides a logistics orchestration platform connecting shippers with third-party courier providers. We act as a technology intermediary; carriage is performed by the selected courier." },
      { h: "2. Accounts", p: "You are responsible for activity under your account and for the accuracy of shipment data you submit." },
      { h: "3. Payments", p: "Shipping charges are prepaid via wallet. Applicable taxes including GST are shown before booking." },
      { h: "4. Liability", p: "Liability for loss or damage in transit is governed by the terms of the courier performing carriage." },
    ],
  },
  privacy: {
    title: "Privacy Policy", sub: "Last updated: June 2026",
    sections: [
      { h: "Data we collect", p: "Account details, shipment addresses, transaction records and usage telemetry required to operate the platform." },
      { h: "How we use it", p: "To fulfil shipments, calculate rates, provide tracking, process payments and improve the service. We do not sell personal data." },
      { h: "Security", p: "Credentials and provider secrets are stored server-side with access controls, audit logging and encrypted transport." },
    ],
  },
  "refund-policy": {
    title: "Refund Policy", sub: "Last updated: June 2026",
    sections: [
      { h: "Cancellations", p: "Shipments cancelled before pickup are refunded in full to your SHIPANY wallet after courier confirmation; most confirmations are instant." },
      { h: "Failed deliveries", p: "RTO shipments are billed per the courier's rate card; any refundable component is credited to your wallet." },
      { h: "Disputes", p: "Raise a support ticket within 7 days of the charge for billing disputes." },
    ],
  },
  "shipping-policy": {
    title: "Shipping Policy", sub: "Last updated: June 2026",
    sections: [
      { h: "Coverage", p: "Domestic coverage across 27,000+ pincodes in five zones; international lanes rolling out progressively." },
      { h: "Timelines", p: "Estimated delivery times shown at booking are courier estimates and may vary by lane and season." },
      { h: "Prohibited items", p: "Dangerous goods, currency, and items prohibited by law must not be shipped. Accounts violating this may be suspended." },
    ],
  },
  "cookie-policy": {
    title: "Cookie Policy", sub: "Last updated: June 2026",
    sections: [
      { h: "What we use", p: "Strictly necessary httpOnly cookies for authentication sessions and security. We do not use advertising trackers." },
      { h: "Managing cookies", p: "You can clear cookies in your browser settings; the dashboard requires session cookies to function." },
    ],
  },
  grievance: {
    title: "Grievance Officer", sub: "In accordance with the Information Technology Act, 2000.",
    sections: [
      { h: "Contact", p: "Grievance Officer, SHIPANY — grievance@shipany.in. We acknowledge complaints within 24 hours and resolve within 15 days." },
      { h: "Escalations", p: "If your issue is unresolved, write to support@shipany.in with your ticket ID for priority review." },
    ],
  },
  contact: {
    title: "Contact Us",
    sub: "Talk to the SHIPANY team.",
    contact: true,
  },
};

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill all fields");
    toast.success("Message received! Our team will reply within 24 hours.");
    setForm({ name: "", email: "", message: "" });
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-10 max-w-xl space-y-4 rounded-2xl border border-white/10 bg-shipany-panel p-6" data-testid="contact-form">
      {[["name", "Your name"], ["email", "Email address"]].map(([k, ph]) => (
        <input key={k} data-testid={`contact-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
          className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-shipany-cyan focus:outline-none" />
      ))}
      <textarea data-testid="contact-message-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" rows={5}
        className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-shipany-cyan focus:outline-none" />
      <button data-testid="contact-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-shipany-blue py-3 text-sm font-semibold transition-colors hover:bg-[#0648B3]">
        <Send className="h-4 w-4" /> Send Message
      </button>
      <p className="text-center text-xs text-slate-500">or email support@shipany.in</p>
    </form>
  );
}

export default function StaticPage({ pageKey }) {
  const page = PAGES[pageKey] || PAGES.about;
  const [openFaq, setOpenFaq] = useState(0);
  return (
    <MarketingLayout>
      <section className="hero-grid py-20">
        <div className="mx-auto max-w-4xl px-5">
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">{page.title}</h1>
          <p className="mt-4 text-base text-slate-300 sm:text-lg">{page.sub}</p>
        </div>
      </section>
      <section className="bg-shipany-mist py-16 text-shipany-navy">
        <div className="mx-auto max-w-4xl px-5">
          {page.contact && <ContactForm />}
          {page.faq && (
            <div className="space-y-3">
              {page.faq.map(([q, a], i) => (
                <div key={q} className="rounded-xl border border-slate-200 bg-white">
                  <button data-testid={`faq-item-${i}`} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-semibold">
                    {q}<ChevronDown className={`h-4 w-4 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && <p className="px-5 pb-4 text-sm leading-relaxed text-slate-500">{a}</p>}
                </div>
              ))}
            </div>
          )}
          {page.sections && (
            <div className="space-y-8">
              {page.sections.map((s) => (
                <div key={s.h} className="rounded-2xl border border-slate-200 bg-white p-7">
                  <h2 className="font-display text-xl font-bold">{s.h}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">{s.p}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
